import * as vp from 'vopay';
import { Box, Button, SubTitle, Table, Toggle, BackButton } from 'vopay';

import React, { FunctionComponent, useEffect } from 'react';
import { Formik } from 'formik';

import type { AccountUserPermissionsHandlers } from '../types';

import buildAccountUserPermissionRows from './buildAccountUserPermissionRows.helper';
import { usePartnerData } from '@/providers/PartnerData.provider';

type AccountUserPermissionRows = {
  label: vp.AccountUserPermissionsLabel;
  id: vp.AccountUserPermissionsId;
  icon: vp.Icons | undefined;
};

type AccountUserPermissionsFormProps = {
  accountUserPermissions: vp.AccountUserPermissionsMap;
  handlers: AccountUserPermissionsHandlers;
  accountUserEmailAddress: string;
};

type FC = FunctionComponent<AccountUserPermissionsFormProps>;

const AccountUserPermissionsForm: FC = (props: AccountUserPermissionsFormProps) => {
  const { accountUserPermissions, handlers, accountUserEmailAddress } = props;
  const accountUserPermissionRows = buildAccountUserPermissionRows();
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  useEffect(() => {
    handlers.initializeAccountUserPermissions(accountUserPermissions);
  }, []);

  return (
    <Formik enableReinitialize initialValues={accountUserPermissions} onSubmit={handlers.onAccountUserPermissionSave}>
      {({ values, handleChange, handleBlur, handleSubmit }) => {
        /*

          ▼ setup types for form handlers ▼

        */

        type ToggleChangeProps = {
          id?: vp.AccountUserPermissionsId;
          isEnabled?: boolean;
        };

        type ToggleHandler = (event: React.ChangeEvent<HTMLInputElement>, props: ToggleChangeProps) => void;

        /*

          ▼  form handlers ▼

        */

        const handleToggleChange: ToggleHandler = (event, { id: permissionId, isEnabled }) => {
          handleChange(event);

          if (permissionId && typeof isEnabled === 'boolean') {
            handlers.onAccountUserPermissionChange(permissionId, isEnabled);
          }
        };

        /*

          ▼  setup table for boolean permissions ▼

        */

        const togglePermissionsTableProps: vp.TableProps<AccountUserPermissionRows> = {
          id: 'user-toggle-permissions',
          version: '2',
          zebra: true,
          columns: [
            {
              id: 'permission',
              header: 'Toggle Permissions',
              field: 'label',
              align: 'left',
            },
            {
              id: 'toggle',
              header: '',
              template: ({ data }) => (
                <Box jc="fe" pt="sm" pb="sm">
                  <Toggle
                    key={data.id}
                    name={data.id}
                    onBlur={handleBlur}
                    onChange={handleToggleChange}
                    value={!!values[data.id]}
                  />
                </Box>
              ),
            },
          ],
          rows: accountUserPermissionRows.map((row) => ({
            id: row.id,
            data: row,
          })),
        };

        /*

          ▼  render form ▼

        */

        return (
          <form onSubmit={handleSubmit}>
            <Box mt="lg" mb="md" gap="md" ai="center">
              <BackButton variant="arrow" backButtonEnabled onClick={handlers.onBack} size="sm" />
              <SubTitle subTitle={`User Permissions - ${accountUserEmailAddress}`} />
            </Box>
            <Table {...togglePermissionsTableProps} />
            <Box mt="md" jc="fe">
              <Button id="accountPermissionsForm-submit" type="submit" label="Save" isDisabled={isRoleViewOnly} />
            </Box>
          </form>
        );
      }}
    </Formik>
  );
};

export default AccountUserPermissionsForm;
